import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Calendar, Building2, MapPin, Wrench, ArrowLeft } from "lucide-react";
import { CHANTIERS, OPERATION_TEXT, PHOTOS_BY_OPERATION, SERVICES } from "../data/siteData";
import { useSeo } from "../hooks/useSeo";
import { PageHeader, CtaBand } from "../components/PageHeader";

const Fact = ({ icon: Icon, label, value, testId }) => (
  <div className="bg-[#141210] border border-[#2E2820] p-5">
    <div className="flex items-center gap-2 text-[#D4A537] text-xs uppercase tracking-wider mb-2"><Icon className="w-4 h-4" />{label}</div>
    <p data-testid={testId} className="text-[#F5EFE3] font-semibold">{value}</p>
  </div>
);

export default function ChantierDetail() {
  const { slug } = useParams();
  const c = CHANTIERS.find((x) => x.slug === slug);
  const desc = c ? `Chantier ${c.chantier} (${c.lieu}) : ${c.operation} réalisé par A.C. ELEC 94 pour ${c.client}.` : "";
  useSeo(c ? `Chantier ${c.chantier}` : "Réalisations", desc, `/realisations/${slug}`);
  if (!c) return <Navigate to="/realisations" replace />;
  const photos = PHOTOS_BY_OPERATION[c.operation] || [];
  const service = SERVICES.find((s) => s.operations.includes(c.operation));
  const aVenir = Boolean(c.aVenir);

  return (
    <main data-testid={`chantier-page-${c.slug}`} className="pt-20">
      <PageHeader crumbs={[{ label: "Réalisations", to: "/realisations" }, { label: c.chantier }]} label={c.secteur} title={c.chantier} />
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-16 reveal">
            <Fact icon={Calendar} label="Période" value={c.periode} testId="chantier-periode" />
            <Fact icon={Building2} label="Client" value={c.client} testId="chantier-client" />
            <Fact icon={Wrench} label="Opération" value={c.operation} testId="chantier-operation" />
            <Fact icon={MapPin} label="Localisation" value={c.lieu} testId="chantier-lieu" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 mb-20">
            <div className="reveal space-y-5 text-[#A8A091] text-lg leading-relaxed">
              <h2 className="font-['Oswald'] text-3xl font-bold text-[#F5EFE3]">Travaux {aVenir ? "prévus" : "réalisés"}</h2>
              <p>Pour le compte de <strong className="text-[#F5EFE3]">{c.client}</strong>, A.C. ELEC 94 {aVenir ? "interviendra" : "intervient"} sur ce chantier ({c.secteur.toLowerCase()}) pour le {OPERATION_TEXT[c.operation] || c.operation}.</p>
              <p>Nos équipes y appliquent les mêmes exigences que sur tous nos chantiers : sécurité, repérage rigoureux, autocontrôles et respect du planning fixé par le donneur d'ordre.</p>
              {service && (
                <Link to={`/services/${service.slug}`} data-testid="chantier-service-link" className="inline-flex items-center gap-2 text-[#D4A537] font-semibold hover:gap-3 transition-all">
                  Découvrir notre prestation : {service.titre} →
                </Link>
              )}
            </div>
            <div className="reveal grid grid-cols-2 gap-4 content-start">
              {photos.map((p, i) => (
                <img key={p} src={p} alt={`${c.operation} - exemple d'intervention A.C. ELEC 94`} className={`w-full object-cover border border-[#2E2820] ${i === 0 ? "col-span-2 h-72" : photos.length === 2 ? "col-span-2 h-56" : "h-44"}`} />
              ))}
              <p className="col-span-2 text-xs text-[#8C8578]">Photos illustratives de nos interventions de type « {c.operation} ».</p>
            </div>
          </div>
          <Link to="/realisations" data-testid="chantier-back-link" className="inline-flex items-center gap-2 text-[#A8A091] hover:text-[#D4A537] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour aux réalisations
          </Link>
        </div>
      </section>
      <CtaBand />
    </main>
  );
}
